/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import { cloneDeep, find, forEach, isArray, isEmpty, isNil, isString, map } from 'lodash';
import { check, Match } from 'meteor/check';
import path from 'path';
import turf from '@turf/turf';

import Annotations from '../../Annotations/Annotations';
import Projects from '../../Projects/Projects';
import { FeatureCollectionPoints } from '../../SchemaUtilities/GeoJSONSchema';
import * as videoOps from '../video-operations';
import trackFeaturePointPropertiesSchema from '../VideoCoordinatesSchema';
import Videos from '../Videos';

import checkAuthentication from '../../../modules/server/check-authentication';
import { deleteFolderRecursiveSync } from '../../../modules/server/file-system';
import { cleanObjectForExport } from '../../../modules/export-import-files';
import handleMethodException from '../../../modules/handle-method-exception';

export const editVideoSchema = Videos.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted',
  'shared',
  'audience'
);
editVideoSchema.extend({ _id: String });

/**
 * Finds a video in the database with that id and returns it.
 *
 * @param {string} videoId - The video id.
 * @returns {object} The video object that has that id after cleaning some properties.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const findOne = videoId => {
  check(videoId, String);
  try {
    const video = Videos.findOne({ _id: videoId });
    const cleanVideo = cloneDeep(video);
    delete cleanVideo.owner;
    delete cleanVideo.project;
    delete cleanVideo.geometryFileName;
    delete cleanVideo.logFileName;
    return cleanVideo;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Inserts the video in the database. Also creates a file in the server with the coordinates data after validating them.
 *
 * @async
 * @param {object} video - The video object.
 * @param {string} userId - The user id that owns the video.
 * @returns {string} The id of the new video inserted in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (video, userId) => {
  check(video, Object);
  check(userId, String);
  try {
    checkAuthentication();
    const clonedVideo = cloneDeep(video);
    delete clonedVideo.densePointFeatureCollection;
    if (videoOps.areCoordinatesFromPoint(video)) {
      delete clonedVideo.track;
      delete clonedVideo.controlPoints;
    }

    let defaultFeatureArray = [
      turf.point(videoOps.getLatLng0(clonedVideo), {
        distanceTravelled: 0,
        isControlPoint: false,
        timeUS: 0
      })
    ];
    if (!isEmpty(video.controlPoints)) {
      defaultFeatureArray = defaultFeatureArray.concat(video.controlPoints.features);
    }

    const features = turf.featureCollection(defaultFeatureArray);

    Videos.schema.validate(clonedVideo, {
      check,
      keys: [
        'name',
        'description',
        'project',
        'url',
        'geometryFileName',
        'logFileName',
        'noCoordinates',
        'latLng0',
        'type',
        'tags',
        'track',
        'controlPoints',
        'coordinatesDataType',
        'annotationTemplates'
      ]
    });
    FeatureCollectionPoints.validate(features);
    turf.propEach(features, currentProperties => {
      trackFeaturePointPropertiesSchema.validate(currentProperties);
    });
    const videoId = Videos.insert({ owner: userId, ...clonedVideo });
    return videoId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the video in the database. Also creates a file in the server with the new coordinates data after validating them.
 *
 * @async
 * @param {object} video - The new video object.
 * @param {string} userId - The user id that owns the video.
 * @returns {string} The id of the updated video in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = async (video, userId) => {
  check(video, Object);
  check(userId, String);
  try {
    checkAuthentication();
    const clonedVideo = cloneDeep(video);
    delete clonedVideo.densePointFeatureCollection;
    if (videoOps.areCoordinatesFromPoint(video)) {
      delete clonedVideo.track;
      delete clonedVideo.controlPoints;
    }
    editVideoSchema.validate(clonedVideo);
    const videoId = videoOps.getId(video);

    Videos.update(videoId, { $set: clonedVideo });
    return videoId;
    // Return _id so we can redirect to video after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles a video or an array of videos as shared or not in the database.
 *
 * @param {string|string[]} videoId - The video id or array of video ids to toggle shared.
 * @param {boolean} isShared - The new shared value.
 * @param {Array} audience - The new shared audience array.
 */
export const toggleShared = (videoId, isShared, audience) => {
  check(videoId, Match.OneOf(String, Array));
  check(isShared, Boolean);
  check(audience, Array);
  const isSharedPrivate = !!(audience[0] === 'private' || audience[0] === 'privateLimited');

  try {
    checkAuthentication();
    if (isString(videoId)) {
      Videos.update(videoId, {
        $set: { shared: isShared, sharedPrivate: isSharedPrivate, audience }
      });
    }
    if (isArray(videoId)) {
      forEach(videoId, video =>
        Videos.update(video, {
          $set: { shared: isShared, sharedPrivate: isSharedPrivate, audience }
        })
      );
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Given a video id and a project id, this method
 * searches for the video in the database and copies the video in the
 * provided project. If the detination project is shared the
 * new video copied is also shared.
 *
 * @param {string} userId - The user id.
 * @param {string} videoId - The video id.
 * @param {string} destProjectId - The project id.
 * @returns {object} Returns the ids of the original and new videos.
 */
const copyOne = async (userId, videoId, destProjectId) => {
  try {
    checkAuthentication();
    const project = Projects.findOne({ _id: destProjectId });
    const copyVideoData = Videos.findOne({ _id: videoId });
    copyVideoData.name = `${copyVideoData.name} - copy`;
    delete copyVideoData.owner;
    delete copyVideoData.project;
    delete copyVideoData._id;
    delete copyVideoData.createdAt;
    delete copyVideoData.updatedAt;
    delete copyVideoData.shared;
    delete copyVideoData.audience;
    copyVideoData.project = destProjectId;
    const destVideoId = Videos.insert({ owner: userId, ...copyVideoData });

    if (project.shared) {
      toggleShared(destVideoId, project.shared, ['limited']);
    }
    return {
      originVideoId: videoId,
      destVideoId
    };
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Given a video or an array of videos, copies each video and returns the
 * id of the copied video or an array with the copied video ids.
 *
 * @param {string} userId - The user id.
 * @param {string|string[]} videoToCopy - The video id or an array of video ids.
 * @param {string} destProjectId - The project id.
 * @returns {object|object[]} An object or array of objects with the original and new videos ids or an array of the same.
 */
export const copy = async (userId, videoToCopy, destProjectId) => {
  check(userId, String);
  check(videoToCopy, Match.OneOf(String, Array));
  check(destProjectId, String);
  try {
    checkAuthentication();
    if (isString(videoToCopy)) {
      const newVideo = await copyOne(userId, videoToCopy, destProjectId);
      return newVideo;
    }
    if (isArray(videoToCopy)) {
      const newVideosPromises = map(videoToCopy, videoId =>
        copyOne(userId, videoId, destProjectId)
      );
      const newVideos = await Promise.all(newVideosPromises);
      return newVideos;
    }
    throw new Error('Not a Valid Video');
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Given a project, copies each of its videos and returns the
 * an array with the copied video ids.
 *
 * @param {string} projectId - The origin project id.
 * @returns {object[]} An array of objects with the original and new videos ids.
 */
export const videosFromProject = async projectId => {
  check(projectId, String);
  try {
    checkAuthentication();
    const videosToCopy = Videos.find({ project: projectId }).fetch();
    return videosToCopy;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Given a project, copies each of its videos and returns the
 * an array with the copied video ids.
 *
 * @param {string} userId - The user id.
 * @param {string} originProjectId - The origin project id.
 * @param {string} destProjectId - The destination project id.
 * @returns {object[]} An array of objects with the original and new videos ids.
 */
export const copyFromProject = async (userId, originProjectId, destProjectId) => {
  check(userId, String);
  check(originProjectId, String);
  check(destProjectId, String);
  try {
    checkAuthentication();
    const videosToCopy = Videos.find({ project: originProjectId }).fetch();
    const videosToCopyIds = map(videosToCopy, '_id');
    const newVideosIds = await copy(userId, videosToCopyIds, destProjectId);
    return newVideosIds;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes de video, so that it's moved
 * to the trash.
 *
 * @param {string} videoId - The video id.
 */
export const softDelete = videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    Videos.update(videoId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Changes the video option in the database.
 *
 * @param {string} videoId - The video id.
 * @param {string} option - The new video option.
 */
export const setOption = (videoId, option) => {
  check(videoId, String);
  check(option, String);
  try {
    checkAuthentication();
    Videos.update(videoId, { $set: { option } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the video from the database.
 * This means setting the deleted key to 'no'.
 *
 * @param {string} videoId - The video id.
 */
export const restore = videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    Videos.update(videoId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the video from the database.
 * Deletes the annotations for that video from the database as well.
 * Also deletes the files associated with that video.
 *
 * @param {string} videoId - The video id.
 * @returns {object} Object that contains 2 keys, one with the number of deleted files and another with the number of deleted folders.
 */
export const hardDelete = videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    Annotations.remove({
      owner: Meteor.userId(),
      'geoJSON.properties.video': videoId
    });
    const video = Videos.findOne(videoId);
    const videoDir = path.join(
      Meteor.settings.private.config.dataPath,
      'users',
      Meteor.userId(),
      'projects',
      videoOps.getProject(video),
      'videos',
      videoId
    );
    Videos.remove(videoId);
    return deleteFolderRecursiveSync(videoDir);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Get the suggestion for the tags from the database videos.
 *
 * @returns {object[]} An array with the existing tags from the videos in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getTagSuggestions = () => {
  const suggestions = [];
  const videos = Videos.find({ deleted: { $eq: 'no' }, shared: { $eq: true } }).fetch();
  forEach(videos, video => {
    forEach(videoOps.getTags(video), tag => {
      if (isNil(find(suggestions, tag))) suggestions.push(tag);
    });
  });
  return suggestions;
};

/**
 * Fetchs all the annotations from the video to export.
 *
 * @param {string} videoId - The video id.
 * @returns {object} Returns an object that contains all video annotations.
 * @throws Error if something went wrong.
 */
export const exportAnnotations = videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    const video = Videos.findOne(videoId);
    if (isNil(video)) throw new Error('No Video Found');
    const findAnnotations = Annotations.find({ 'geoJSON.properties.video': videoId }).fetch();
    const exportItem = { annotations: findAnnotations };
    cleanObjectForExport(exportItem);
    return exportItem;
  } catch (error) {
    handleMethodException(error);
  }
};
